import { create } from 'zustand';
import { GroupMemberData, GroupMemberPropertyVisibleData, GroupMemberTagData, SocialMedia } from '../dto/EntityRepository';
import { GroupMemberStatus } from '../enum/EnumRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface GroupMemberState {
  groupMemberList: GroupMemberData[];
  focusedGroupMemberId: string | null;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  setGroupMemberList: (
    groupMemberList: GroupMemberData[],
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  replaceGroupMemberList: (groupMemberList: GroupMemberData[]) => void;
  beginGroupMemberListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setGroupMemberListError: () => void;
  addGroupMember: (groupMember: GroupMemberData) => void;
  updateGroupMember: (groupMember: GroupMemberData) => void;
  afterUpdateGroupMemberBulk: (updatedGroupMemberList: GroupMemberData[]) => void;
  updateGroupMemberPropertyVisible: (
    groupMemberId: string,
    propertyVisible: GroupMemberPropertyVisibleData,
  ) => void;
  updateGroupMemberStatus: (groupMemberId: string, status: GroupMemberStatus) => void;
  updateGroupMemberConnection: (
    groupMemberId: string,
    linkedMemberAui: string,
    linkedMemberHandle: string,
  ) => void;
  syncGroupMemberTag: (tag: GroupMemberTagData) => void;
  removeGroupMemberTag: (tagId: number) => void;
  updateGroupMemberLocal: (groupMemberId: string, updates: Partial<GroupMemberData>) => void;
  removeGroupMember: (groupMemberId: string) => void;
  setFocusedGroupMemberId: (groupMemberId: string | null) => void;
  clearGroupMembers: () => void;
}

const isSameSns = (prev: SocialMedia, next: SocialMedia): boolean =>
  prev.twitter === next.twitter &&
  prev.instagram === next.instagram &&
  prev.facebook === next.facebook &&
  prev.threads === next.threads &&
  prev.behance === next.behance &&
  prev.youtube === next.youtube &&
  prev.vimeo === next.vimeo &&
  prev.url1 === next.url1;

const isSamePropertyVisible = (
  prev: GroupMemberPropertyVisibleData,
  next: GroupMemberPropertyVisibleData,
): boolean =>
  prev.id === next.id &&
  prev.email === next.email &&
  prev.contact === next.contact &&
  prev.twitter === next.twitter &&
  prev.instagram === next.instagram &&
  prev.facebook === next.facebook &&
  prev.youtube === next.youtube &&
  prev.url1 === next.url1;

const isSameTags = (
  prev: GroupMemberTagData[],
  next: GroupMemberTagData[],
): boolean =>
  prev.length === next.length &&
  prev.every((tag, index) => {
    const other = next[index];
    return tag.id === other.id && tag.name === other.name && tag.index === other.index;
  });

const isSameGroupMember = (prev: GroupMemberData, next: GroupMemberData): boolean =>
  prev.id === next.id &&
  prev.name === next.name &&
  prev.nameEn === next.nameEn &&
  prev.role === next.role &&
  prev.country === next.country &&
  prev.year === next.year &&
  prev.email === next.email &&
  prev.contact === next.contact &&
  prev.description === next.description &&
  prev.descriptionEn === next.descriptionEn &&
  prev.status === next.status &&
  prev.linkedMemberAui === next.linkedMemberAui &&
  prev.linkedMemberHandle === next.linkedMemberHandle &&
  prev.index === next.index &&
  isSameSns(prev.sns, next.sns) &&
  isSamePropertyVisible(prev.propertyVisible, next.propertyVisible) &&
  isSameTags(prev.tags, next.tags);

const isSameGroupMemberList = (
  prev: GroupMemberData[],
  next: GroupMemberData[],
): boolean =>
  prev.length === next.length &&
  prev.every((member, index) => isSameGroupMember(member, next[index]));

const withUnchangedFlag = (groupMemberList: GroupMemberData[]): GroupMemberData[] =>
  groupMemberList.map((member) => ({ ...member, hasChanged: false }));

export const useGroupMemberStore = create<GroupMemberState>((set) => ({
  groupMemberList: [],
  focusedGroupMemberId: null,
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  beginGroupMemberListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setGroupMemberList: (groupMemberList, ownerAui, scope) =>
    set((state) => {
      const normalized = withUnchangedFlag(groupMemberList);
      if (
        state.status === 'success' &&
        state.listOwnerAui === ownerAui &&
        state.listScope === scope &&
        isSameGroupMemberList(state.groupMemberList, normalized)
      ) {
        return state;
      }
      return {
        groupMemberList: normalized,
        focusedGroupMemberId: normalized[0]?.id ?? null,
        listOwnerAui: ownerAui,
        listScope: scope,
        status: 'success',
      };
    }),
  replaceGroupMemberList: (groupMemberList) => set({
    groupMemberList: withUnchangedFlag(groupMemberList),
    focusedGroupMemberId: groupMemberList[0]?.id ?? null,
  }),
  setGroupMemberListError: () => set({ status: 'error' }),
  addGroupMember: (groupMember) => set((state) => ({
    groupMemberList: [
      ...state.groupMemberList.filter((member) => member.id !== groupMember.id),
      { ...groupMember, hasChanged: false },
    ],
    focusedGroupMemberId: groupMember.id,
  })),
  updateGroupMember: (groupMember) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) =>
      member.id === groupMember.id ? { ...groupMember, hasChanged: false } : member
    ),
  })),
  afterUpdateGroupMemberBulk: (updatedGroupMemberList) =>
    set((state) => {
      if (updatedGroupMemberList.length === 0) return state;
      const updatedById = new Map(updatedGroupMemberList.map((member) => [member.id, member]));
      return {
        groupMemberList: state.groupMemberList.map((member) => {
          const updated = updatedById.get(member.id);
          return updated ? { ...updated, hasChanged: false } : member;
        }),
      };
    }),
  updateGroupMemberPropertyVisible: (groupMemberId, propertyVisible) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) =>
      member.id === groupMemberId ? { ...member, propertyVisible } : member
    ),
  })),
  updateGroupMemberStatus: (groupMemberId, status) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) =>
      member.id === groupMemberId
        ? { ...member, status }
        : member
    ),
  })),
  updateGroupMemberConnection: (
    groupMemberId,
    linkedMemberAui,
    linkedMemberHandle,
  ) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) =>
      member.id === groupMemberId
        ? { ...member, linkedMemberAui, linkedMemberHandle }
        : member
    ),
  })),
  syncGroupMemberTag: (tag) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) => ({
      ...member,
      tags: member.tags.map((memberTag) => memberTag.id === tag.id ? tag : memberTag),
    })),
  })),
  removeGroupMemberTag: (tagId) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) => ({
      ...member,
      tags: member.tags.filter((tag) => tag.id !== tagId),
    })),
  })),
  updateGroupMemberLocal: (groupMemberId, updates) => set((state) => ({
    groupMemberList: state.groupMemberList.map((member) =>
      member.id === groupMemberId
        ? { ...member, ...updates, hasChanged: true }
        : member
    ),
  })),
  removeGroupMember: (groupMemberId) => set((state) => {
    const nextList = state.groupMemberList.filter((member) => member.id !== groupMemberId);
    return {
      groupMemberList: nextList,
      focusedGroupMemberId: state.focusedGroupMemberId === groupMemberId
        ? nextList[0]?.id ?? null
        : state.focusedGroupMemberId,
    };
  }),
  setFocusedGroupMemberId: (groupMemberId) => set({ focusedGroupMemberId: groupMemberId }),
  clearGroupMembers: () => set((state) => {
    if (
      state.groupMemberList.length === 0 &&
      state.focusedGroupMemberId === null &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      groupMemberList: [],
      focusedGroupMemberId: null,
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));
